
import { useState } from 'react';

interface Artist {
  bioLong: string;
  name: string;
  presskitUrl?: string | null;
}

interface Collaborator {
  id: string;
  name: string;
  slug: string;
  avatarUrl?: string | null;
  trackCount: number;
  releaseCount: number;
}

interface AboutSectionProps {
  artist: Artist;
  collaborators: Collaborator[];
}

const PLACEHOLDER_AVATAR = '/images/254artistdefault.png';

export default function AboutSection({ artist, collaborators }: AboutSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldShowReadMore = artist.bioLong.length > 1000;
  const displayText = isExpanded || !shouldShowReadMore
    ? artist.bioLong 
    : `${artist.bioLong.substring(0, 1000)}...`;

  return (
    <section className="bg-white rounded-lg border border-neutral-200 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">About {artist.name}</h2>
      </div>

      <div className="prose prose-gray max-w-none">
        <p className="text-gray-600 leading-relaxed whitespace-pre-line">
          {displayText}
        </p>
        
        {shouldShowReadMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium text-sm transition-colors"
          >
            {isExpanded ? 'Read less' : 'Read more'}
          </button>
        )}
      </div>

      {/* Collaborators Section */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4">Frequent Collaborators</h3>
        {collaborators.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {collaborators.map((collaborator) => (
              <a
                key={collaborator.id}
                href={`/artists/${collaborator.slug}`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-800 hover:border-emerald-300 hover:text-emerald-700 transition-colors bg-gray-50"
              >
                <img
                  src={collaborator.avatarUrl || PLACEHOLDER_AVATAR}
                  alt={collaborator.name}
                  className="w-8 h-8 rounded-full object-cover border border-gray-200 bg-white"
                  loading="lazy"
                />
                <span className="font-semibold">{collaborator.name}</span>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {collaborator.trackCount} track{collaborator.trackCount === 1 ? '' : 's'}
                  {collaborator.releaseCount > 0
                    ? ` · ${collaborator.releaseCount} release${collaborator.releaseCount === 1 ? '' : 's'}`
                    : ''}
                </span>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-600">No collaborators yet.</p>
        )}
      </div>

      {/* Press Kit */}
      {artist.presskitUrl && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <a
            href={artist.presskitUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
          >
            <i className="ri-download-line mr-2"></i>
            Download Press Kit
          </a>
        </div>
      )}
    </section>
  );
}
